import { FC, useEffect, useRef, useState } from 'react';
import { Checkbox } from 'antd';
import { extractTextFromHtml } from 'utils/commonUtil';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import HalfScreenModal from 'components/HalfScreenModal/HalfScreenModal';
import RichTextEditor from 'components/RichTextEditor/RichTextEditor';
import { createRichMedia } from 'modules/newHireChecklist/slices/nhcSlice';
import { SectionName } from 'modules/home/constants';
import DBGTemplateConfirmationModal from 'modules/disclosures/component/DBGTemplateComfirmationModal/DBGTemplateConfirmationModal';
import {
  getDisclosureTemplate,
  saveDisclosureTemplate,
} from 'modules/auth/services/BenguideService';

type ResourceTextEditorProps = {
  isOpenModal: boolean;
  closeModal: Function;
  editorContent: string;
  updateEditorContent: (data: string) => void;
  resetToDefault: () => void;
  isResetEnabled: boolean;
  benguideId?: string;
  defaultContent?: string;
  resetToCurrentValue: Function;
  onCreate: Function;
  onTemplateUpdate?: (content: string) => void;
};

const DisclosuresTextEditor: FC<ResourceTextEditorProps> = (
  props: ResourceTextEditorProps
) => {
  const {
    isOpenModal,
    closeModal,
    editorContent,
    updateEditorContent,
    benguideId,
    defaultContent,
    resetToCurrentValue,
    onCreate,
    onTemplateUpdate,
  } = props;

  const textEditorRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const organizationId = useAppSelector((state) => state.benguide.data?.orgId);

  const loggedInUserType = useAppSelector(
    (state) => state.benguide.loggedInUserType
  );

  const loggedInUserSubtype = useAppSelector(
    (state) => state.benguide.loggedInUserSubtype
  );

  const isBrokerAdmin =
    loggedInUserSubtype === 'BROKER_ADMIN' ||
    loggedInUserSubtype === 'BROKER_SUPER_ADMIN' ||
    loggedInUserType === 'PLATFORM_ADMIN';

  const [saveAsDefault, setSaveAsDefault] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [pendingContent, setPendingContent] = useState<string>('');
  const [hasContent, setHasContent] = useState<boolean>(() => {
    const content = editorContent || defaultContent || '';
    const textContent = extractTextFromHtml(content);
    return textContent.length > 0;
  });

  const handleResetToDefault = async () => {
    if (!organizationId) return;

    try {
      const response = await getDisclosureTemplate(organizationId);
      if (response?.data) {
        const templateContent = response.data;
        // Update the editor
        const editorInstance =
          textEditorRef.current?.getEditorRef()?.current?.editor;
        if (editorInstance) {
          editorInstance.setContent(templateContent);
          handleEditorChange(templateContent);
        }
      }
    } catch (error) {
      console.error('Error fetching default template:', error);
    }
  };

  const closeOverviewModal = () => {
    resetToCurrentValue();
    if (textEditorRef.current) {
      const resetContent = defaultContent || '';
      textEditorRef.current.clearTextEditorContent(resetContent);

      const editorInstance =
        textEditorRef.current?.getEditorRef()?.current?.editor;
      if (editorInstance) {
        editorInstance.setContent(resetContent);
      }
    }
    closeModal();
  };

  const handleEditorChange = (data: string) => {
    updateEditorContent(data);
    // Check if editor has meaningful content (not just empty tags or whitespace)
    const textContent = data
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, '')
      .trim();
    setHasContent(textContent.length > 0);
  };

  const saveDisclosureContent = async (content: string) => {
    if (!benguideId) return;

    try {
      await new Promise((resolve) => {
        dispatch(
          createRichMedia(
            benguideId,
            SectionName.DISCLOSURES_TEXT,
            content,
            () => {
              if (onCreate) {
                onCreate(content);
              }
              resolve(null);
            }
          )
        );
      });
    } catch (error) {
      console.error('Error saving disclosures content:', error);
      throw error;
    }
  };

  const saveAsDefaultTemplate = async (content: string) => {
    if (!organizationId) {
      console.error('Organization ID is required to save default template');
      return;
    }

    try {
      if (!benguideId) return;
      await saveDisclosureTemplate(organizationId, benguideId, content);

      // Notify parent component about template update
      if (onTemplateUpdate) {
        onTemplateUpdate(content);
      }
    } catch (error) {
      console.error('Error saving default template:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    const currentContent =
      textEditorRef?.current?.getEditorRef()?.current?.editor?.getContent() ||
      editorContent;

    // If "Save as Default" is checked and user is broker admin, show confirmation
    if (saveAsDefault && isBrokerAdmin) {
      setPendingContent(currentContent);
      setShowConfirmation(true);
      return;
    }

    await saveDisclosureContent(currentContent);
    closeModal();
  };

  const handleConfirmSaveAsDefault = async () => {
    setShowConfirmation(false);

    try {
      // Save disclosure content first
      await saveDisclosureContent(pendingContent);

      // Then save as default template
      await saveAsDefaultTemplate(pendingContent);

      closeModal();
    } catch (error) {
      console.error('Error in confirmation handler:', error);
      // Handle error appropriately (show error message to user)
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setPendingContent('');
  };

  useEffect(() => {
    if (isOpenModal) {
      const content = editorContent || defaultContent || '';
      const textContent = extractTextFromHtml(content);
      setHasContent(textContent.length > 0);
    }
  }, [editorContent, defaultContent, isOpenModal]);

  return (
    <>
      <HalfScreenModal
        width={900}
        title="Edit Disclosures"
        visible={isOpenModal}
        onCancel={closeOverviewModal}
        onSubmit={handleSubmit}
        confirmModalShow
        isSubmitDisabled={!hasContent}
        isResetEnabled={!!defaultContent}
        onReset={handleResetToDefault}
        resetText="Reset to Default Template"
        customLeftContent={
          isBrokerAdmin ? (
            <Checkbox
              checked={saveAsDefault}
              onChange={(e) => setSaveAsDefault(e.target.checked)}
            >
              Save as default template
            </Checkbox>
          ) : null
        }
        increaseResetSpacing
        afterClose={() => {
          textEditorRef?.current?.resetUndo();
          textEditorRef?.current?.clearSelection();
          setSaveAsDefault(false);
          setPendingContent('');
          const content = editorContent || defaultContent || '';
          const textContent = extractTextFromHtml(content);
          setHasContent(textContent.length > 0);
        }}
      >
        <RichTextEditor
          initialValue={defaultContent}
          onChange={handleEditorChange}
          defaultText={editorContent}
          ref={textEditorRef}
          masterId={benguideId}
          height={600}
        />
      </HalfScreenModal>

      <DBGTemplateConfirmationModal
        visible={showConfirmation}
        onConfirm={handleConfirmSaveAsDefault}
        onCancel={handleCancelConfirmation}
      />
    </>
  );
};

export default DisclosuresTextEditor;
